import { useFirebaseAuth, db } from 'contexts/FirebaseAuth';
import _ from 'lodash';
import React from 'react';

import {
  collection,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  onSnapshot, // Add this
} from 'firebase/firestore';

import api from 'utils/api';
import mixpanel from 'utils/mixpanelConfig';

const { createContext, useContext, useState, useEffect } = React;

export const UserAuthContext = createContext({});
UserAuthContext.displayName = 'UserAuth';

export const UserAuthProvider = (props) => {
  const { children } = props;
  const { isSignIn, currentUser, anonymousCurrentUser, anonymousSignIn } =
    useFirebaseAuth();

  // TODO: phase out token state, and only rely on currentUser/anonymousUser from FirebaseAuthContext
  const [token, setToken] = useState(null);

  const [user, setUser] = useState(null);
  const [anonymousUser, setAnonymousUser] = useState(null);
  const [userProductCollection, setUserProductCollection] = useState([]);

  const [fetched, setFetched] = useState(null);

  const [waitingForAnonymousSignIn, setWaitingForAnonymousSignIn] =
    useState(false);

  // console.log({ isSignIn, user });
  const isSignInStatusReady =
    !_.isNil(isSignIn) &&
    (isSignIn === true ? anonymousUser || !_.isNil(fetched) : true);

  const [simToken, setSimToken] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('simToken') || null;
    setSimToken(token);
  }, []);

  const getAuthorizationHeader = async ({ allowAnonymous = false } = {}) => {
    if (!_.isEmpty(simToken)) {
      return { Authorization: `${simToken}` };
    }
    if (currentUser) {
      try {
        const idToken = await currentUser.getIdToken();
        setToken(idToken);
        return { Authorization: `Bearer ${idToken}` };
      } catch (error) {}
    }
    if (allowAnonymous && anonymousCurrentUser) {
      try {
        const idToken = await anonymousCurrentUser.getIdToken();
        setToken(idToken);
        return { Authorization: `Bearer ${idToken}` };
      } catch (error) {}
    }
    return {};
  };

  const toggleUserProductCollection = async (productId) => {
    if (!currentUser) return;
    const existedProduct = userProductCollection.includes(productId);
    const collectionRef = collection(db, 'users');
    const userDocRef = doc(collectionRef, currentUser.uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      if (existedProduct) {
        await updateDoc(userDocRef, {
          productCollection: arrayRemove(productId),
        });
      } else {
        await updateDoc(userDocRef, {
          productCollection: arrayUnion(productId),
        });
      }
    } else {
      await setDoc(userDocRef, {
        uid: currentUser.uid,
        email: currentUser.email,
        productCollection: [productId],
      });
    }
  };

  const fetchUser = async () => {
    if (currentUser || simToken) {
      const headers = await getAuthorizationHeader();
      try {
        const { data } = await api.request({
          url: '/api/v2/me',
          headers,
        });
        setUser(data);
        setAnonymousUser(null);
        mixpanel.identify(data.id);
        mixpanel.people.set({});
      } catch (error) {
        setUser(null);
        setAnonymousUser(null);
      } finally {
        setFetched(true);
      }
    } else if (anonymousCurrentUser) {
      setUser(null);
      if (!waitingForAnonymousSignIn) {
        setAnonymousUser(anonymousCurrentUser);
      }
    } else {
      setToken(null);
      setUser(null);
      setAnonymousUser(null);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [currentUser, anonymousCurrentUser, waitingForAnonymousSignIn]);

  useEffect(() => {
    if (user) {
      const anonymousRedeemId = localStorage.getItem('anonymous_redeem_id');
      if (!_.isNil(anonymousRedeemId)) {
        localStorage.removeItem('anonymous_redeem_id');
        (async () => {
          const headers = await getAuthorizationHeader();
          api.request({
            method: 'POST',
            url: '/api/v3/me/linkGroupBuyingOrder',
            data: { id: anonymousRedeemId },
            headers,
          });
        })();
      }
    }
  }, [user]);

  const ensureAuthHeaders = async () => {
    // 已登入（一般或匿名）→ 正常拿 token
    if (currentUser || anonymousCurrentUser) {
      return getAuthorizationHeader({ allowAnonymous: true });
    }

    // 未登入 → 匿名登入，直接從回傳的 user 拿 token
    setWaitingForAnonymousSignIn(true);
    const { user: anonUser } = await anonymousSignIn();
    const token = await anonUser.getIdToken();
    const { data: anonUserRes } = await api.request({
      url: '/api/v2/me',
      headers: { Authorization: `Bearer ${token}` },
    });
    setAnonymousUser(anonUserRes);
    setWaitingForAnonymousSignIn(false);
    return { Authorization: `Bearer ${token}` };
  };

  // 匿名登入
  const isAnonymous = !!anonymousUser;
  // 正式用戶（非匿名）且已登入
  const isFullUser = !!user && !isAnonymous;

  useEffect(() => {
    // console.log({ currentUser });
    let unsubscribe;
    if (currentUser) {
      unsubscribe = onSnapshot(
        doc(db, 'users', currentUser.uid),
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const docData = docSnapshot.data();
            const productCollection = docData.productCollection || [];
            setUserProductCollection(productCollection);
          } else {
            setUserProductCollection([]);
          }
        },
        (error) => {
          console.error('Error listening to user data:', error);
        }
      );
    }
    return () => {
      unsubscribe && unsubscribe();
      setUserProductCollection([]);
    };
  }, [currentUser]);

  // useEffect(() => {
  //   console.log({ userProductCollection });
  // }, [userProductCollection]);

  return (
    <UserAuthContext.Provider
      value={{
        isSignInStatusReady,
        user,
        anonymousUser,
        token,
        fetchUser,
        getAuthorizationHeader,
        ensureAuthHeaders,
        isAnonymous,
        isFullUser,
        userProductCollection,
        token,
        fetchUser,
        getAuthorizationHeader,
        toggleUserProductCollection,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = () => {
  return useContext(UserAuthContext);
};
